import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

const fadeInUp = (duration = 0.8) => ({
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration } },
});

const About = () => {
  return (
    <div className="bg-white text-gray-800">
      {/* ===== SEO ===== */}
      <Helmet>
        <title>About Solonet Properties | Trusted Real Estate in Ghana</title>
        <meta
          name="description"
          content="Learn about Solonet Properties, a trusted real estate company in Ghana. Discover our mission, vision, and values for property solutions."
        />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="About Solonet Properties | Trusted Real Estate in Ghana"
        />
        <meta
          property="og:description"
          content="Solonet Properties specializes in property sales, rentals, and management across Ghana."
        />
        <meta property="og:image" content="/images/about-og.jpg" />
        <meta property="og:url" content="https://solonetproperties.com/about" />
      </Helmet>

      {/* ===== HERO SECTION ===== */}
      <div className="relative w-full h-[60vh] flex items-center justify-center">
        <img
          src="/images/header.jpg"
          alt="About Solonet"
          className="absolute w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative text-white text-4xl md:text-6xl font-bold text-center"
        >
          About Solonet Properties
        </motion.h1>
      </div>

      {/* ===== ABOUT COMPANY ===== */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Left Side - Image */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp(0.8)}
          >
            <img
              src="/images/about.png"
              alt="Solonet Properties"
              className="rounded-2xl shadow-lg"
              loading="lazy"
            />
          </motion.div>

          {/* Right Side - Text */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp(0.8)}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Who We Are</h2>
            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Solonet Properties is a trusted real estate company dedicated to
              providing top-notch property solutions for individuals, families,
              and businesses. We specialize in property sales, rentals, and
              property management, delivering exceptional value to our clients.
            </p>
            <p className="text-lg leading-relaxed text-gray-700">
              With years of experience and a deep understanding of the real
              estate market, our goal is to make property ownership and
              investment seamless, transparent, and rewarding.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== MISSION, VISION & VALUES ===== */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          {/* Mission */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp(0.6)}
            className="bg-white shadow-md rounded-xl p-6 hover:shadow-xl transition"
          >
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">Our Mission</h3>
            <p className="text-gray-600">
              To provide affordable, reliable, and transparent real estate
              solutions that make property ownership simple and accessible for
              everyone.
            </p>
          </motion.div>

          {/* Vision */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp(0.7)}
            className="bg-white shadow-md rounded-xl p-6 hover:shadow-xl transition"
          >
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">Our Vision</h3>
            <p className="text-gray-600">
              To become the leading real estate company, trusted by clients for
              our integrity, innovation, and outstanding service.
            </p>
          </motion.div>

          {/* Values */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp(0.8)}
            className="bg-white shadow-md rounded-xl p-6 hover:shadow-xl transition"
          >
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">Our Values</h3>
            <p className="text-gray-600">
              Integrity, professionalism, and customer satisfaction drive every
              decision we make, ensuring we always put our clients first.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== CALL TO ACTION ===== */}
      <section className="bg-blue-600 py-12 text-center text-white">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">
          Ready to find your dream property?
        </h2>
        <p className="text-lg mb-6">
          Get in touch with us today and let’s make your real estate journey smooth.
        </p>
        <a
          href="/contact"
          aria-label="Contact Solonet Properties"
          className="px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg shadow hover:bg-gray-100 transition"
        >
          Contact Us
        </a>
      </section>
    </div>
  );
};

export default About;
